import { DashboardKpi, DashboardOverview, DashboardSystemMetric } from '../../types/dashboard';

const numberFormatter = new Intl.NumberFormat('en-US');

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatDelta(pct: number | null): string {
  if (pct === null) return 'No prior-period data yet';
  if (pct === 0) return 'Flat vs. prior period';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct}% vs. prior period`;
}

export function formatCurrency(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(
      amountMinor / 100
    );
  } catch {
    return `$${(amountMinor / 100).toFixed(0)}`;
  }
}

export function buildKpis(overview: DashboardOverview): DashboardKpi[] {
  const { kpis } = overview;

  return [
    {
      id: 'active-agents',
      title: 'Active Agents',
      value: formatNumber(kpis.activeAgents.value),
      subtitle: 'Live in your workspace',
      description: 'AI agents currently deployed and available across your workflows and chatbots.',
      variant: 'teal',
    },
    {
      id: 'conversations',
      title: 'Conversations Handled',
      value: formatNumber(kpis.conversationsHandled.value),
      subtitle: `Last ${overview.periodDays} days`,
      description: `${formatDelta(kpis.conversationsHandled.changePct)} — real conversations across chat and voice.`,
      variant: 'dark',
    },
    {
      id: 'leads',
      title: 'Leads Captured',
      value: formatNumber(kpis.leadsCaptured.value),
      subtitle: `Last ${overview.periodDays} days`,
      description: `${formatDelta(kpis.leadsCaptured.changePct)} — inquiries your agents captured directly.`,
      variant: 'violet',
    },
    {
      id: 'success-rate',
      title: 'Workflow Success Rate',
      value: kpis.workflowSuccessRate.value === null ? '—' : `${kpis.workflowSuccessRate.value.toFixed(1)}%`,
      subtitle: kpis.workflowSuccessRate.value === null ? 'No completed runs yet' : `Last ${overview.periodDays} days`,
      description: 'Share of completed workflow runs that finished successfully, not escalated or failed.',
      variant: 'sky',
    },
  ];
}

export function buildBusinessSnapshot(overview: DashboardOverview): DashboardSystemMetric[] {
  return [
    {
      id: 'kb-articles',
      label: 'Knowledge Base Articles',
      value: formatNumber(overview.knowledgeBase.qaPairCount),
      tone: 'primary',
    },
    {
      id: 'handoff-rate',
      label: 'Human Handoff Rate',
      value: `${overview.handoffRate}%`,
      tone: overview.handoffRate > 25 ? 'warning' : 'positive',
    },
    {
      id: 'active-subscriptions',
      label: 'Active Subscriptions',
      value: formatNumber(overview.billing.activeSubscriptions),
      tone: 'primary',
    },
    {
      id: 'mrr',
      label: 'Monthly Recurring Revenue',
      value: formatCurrency(overview.billing.mrrMinor, overview.billing.currency),
      tone: 'positive',
    },
  ];
}

export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
